import React, { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Col, Form, Input, Modal, Row, Select, message } from 'antd';

import { servicesAPI } from '../../api/client';
import { ALLOCATION_LABELS } from './constants';


function optionalText(value) {
  const text = String(value ?? '').trim();
  return text || null;
}


export default function ServiceFormModal({ open, onClose, service = null }) {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const isEdit = Boolean(service?.id);

  const { data: categories } = useQuery({
    queryKey: ['service-categories', { includeInactive: isEdit }],
    queryFn: () => servicesAPI
      .getCategories(isEdit ? { include_inactive: true } : undefined)
      .then(r => r.data),
    enabled: open,
  });

  const selectableCategories = (categories || []).filter(
    category => category.is_active || category.id === service?.category_id
  );

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (service) {
      form.setFieldsValue({
        name: service.name,
        category_id: service.category_id ?? null,
        vendor: service.vendor ?? '',
        website: service.website ?? '',
        onepassword_link: service.onepassword_link ?? '',
        notes: service.notes ?? '',
        allocation_mode: service.allocation_mode || 'named_user',
      });
    }
  }, [open, service, form]);

  const mutation = useMutation({
    mutationFn: (payload) => (
      isEdit
        ? servicesAPI.updateService(service.id, payload)
        : servicesAPI.createService(payload)
    ),
    onSuccess: () => {
      message.success(isEdit ? 'Сервис обновлён' : 'Сервис создан');
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['services-all'] });
      queryClient.invalidateQueries({ queryKey: ['services-stats'] });
      if (service?.id) {
        queryClient.invalidateQueries({ queryKey: ['service-detail', service.id] });
      }
      onClose();
    },
    onError: (error) => message.error(error?.response?.data?.detail || 'Ошибка'),
  });

  const handleOk = async () => {
    const values = await form.validateFields();
    mutation.mutate({
      name: values.name.trim(),
      category_id: values.category_id ?? null,
      vendor: optionalText(values.vendor),
      website: optionalText(values.website),
      onepassword_link: optionalText(values.onepassword_link),
      notes: optionalText(values.notes),
      allocation_mode: values.allocation_mode || 'named_user',
    });
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Редактировать сервис' : 'Новый сервис'}
      onOk={handleOk}
      onCancel={onClose}
      okText={isEdit ? 'Сохранить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={mutation.isPending}
      width={620}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ allocation_mode: 'named_user' }} style={{ marginTop: 16 }}>
        <Row gutter={12}>
          <Col span={14}>
            <Form.Item
              name="name"
              label="Название"
              rules={[{ required: true, whitespace: true, message: 'Укажите название сервиса' }]}
            >
              <Input placeholder="Например: Figma" />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item name="category_id" label="Категория">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Без категории"
                options={selectableCategories.map(category => ({
                  label: category.name,
                  value: category.id,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="allocation_mode"
          label="Тип лицензирования"
          rules={[{ required: true, message: 'Выберите тип лицензирования' }]}
        >
          <Select
            options={Object.entries(ALLOCATION_LABELS).map(([value, label]) => ({ value, label }))}
          />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="vendor" label="Поставщик / разработчик">
              <Input placeholder="Например: Adobe" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="website" label="Сайт">
              <Input placeholder="https://..." />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="onepassword_link" label="1Password">
          <Input placeholder="Ссылка на запись в 1Password" />
        </Form.Item>

        <Form.Item name="notes" label="Примечание">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
